'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { Button } from '@/ui/buttons/button/Button.client'
import { ValidationMessage } from '@/ui/form/validation-message/ValidationMessage.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Loader } from '@/ui/loaders/loader/Loader'
import { ButtonContext, FieldError, Input, SearchField } from 'react-aria-components'
import { useSearchInputValue } from './_hooks/useSearchInputValue'
import './SearchInput.css'
import type { SearchInputProps } from './SearchInput.types'

const searchInputWrapperClassName =
  'search-input-wrapper flex h-6 w-full items-center gap-1 rounded-sm border-px border-solid pr-1 outline outline-2 outline-offset-2 hd:border-1.5'

export function SearchInput({
  placeholder,
  name,
  value,
  defaultValue,
  ref,
  onChange,
  onBlur,
  onSearchAction,
  onResetAction,
  variant = 'instant',
  withSearchButton = false,
  withDebounce,
  minLength = 0,
  errorMessage,
  isDisabled = false,
  isLoading = false,
  resetAriaLabel,
  searchAriaLabel,
  className,
  id,
  'aria-label': ariaLabel,
  'data-testid': dataTestId = 'search-input',
}: SearchInputProps) {
  const { t } = useTranslation(['searchInput'])

  const { currentValue, handleValueChange, handleSubmit, handleClear } = useSearchInputValue({
    value,
    defaultValue,
    minLength,
    withDebounce,
    variant,
    onSearchAction,
    onResetAction,
  })

  const hasError = !!errorMessage
  const isEmpty = currentValue === ''
  const isSearchDisabled = currentValue.length > 0 && currentValue.length < minLength

  return (
    <SearchField
      id={id}
      name={name}
      value={currentValue}
      onChange={handleValueChange}
      onSubmit={handleSubmit}
      onClear={handleClear}
      isDisabled={isDisabled}
      isInvalid={hasError}
      aria-label={ariaLabel ?? placeholder}
      data-testid={dataTestId}
      className={twMerge('search-input flex w-full flex-col', className)}
    >
      <div className={searchInputWrapperClassName}>
        <Input
          ref={ref}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          data-testid={`${dataTestId}/input`}
          className={twMerge(
            'h-full min-w-0 flex-1 bg-clear pl-2 text-md font-medium leading-light text-brand-2-900 outline-none placeholder:text-brand-2-400 data-[disabled]:text-brand-2-400 [&::-webkit-search-cancel-button]:appearance-none',
            // Same radius as the wrapper it now touches, or autofill overflows the rounded corner
            'rounded-l-sm',
          )}
        />

        {isLoading && (
          <span
            aria-hidden
            data-testid={`${dataTestId}/loader`}
            className="mr-1 shrink-0 text-primary"
          >
            <Loader />
          </span>
        )}

        {/* No onPress here on purpose: SearchField publishes its clearButtonProps through ButtonContext,
            which this Button consumes, so it already gets the press handler plus excludeFromTabOrder and
            preventFocusOnPress. Adding our own onPress would override the one react-aria injects */}
        {!isEmpty && !isLoading && (
          <Button
            aria-label={resetAriaLabel ?? t('searchInput:buttons.ariaLabels.reset')}
            className="border-clear"
            data-testid={`${dataTestId}/clear`}
            iconLeft={<Icon.X />}
            intent={INTENT_SAME_AS_WEB}
            isRound
            size="sm"
            type="button"
            variant="quaternary"
          />
        )}

        {/* SearchField publishes clearButtonProps on ButtonContext without a slot, so every descendant
            RA Button inherits them. Resetting the context to {} isolates the magnifier, otherwise it
            would clear the field instead of submitting it */}
        {withSearchButton && (
          <ButtonContext.Provider value={{}}>
            <Button
              aria-label={searchAriaLabel ?? t('searchInput:buttons.ariaLabels.search')}
              data-testid={`${dataTestId}/search`}
              iconLeft={<Icon.Search />}
              intent={INTENT_SAME_AS_WEB}
              isDisabled={isSearchDisabled}
              isLoading={isLoading}
              isRound
              onPress={handleSubmit}
              size="sm"
              type="button"
              variant={isEmpty && !isSearchDisabled ? 'quaternary' : 'primary'}
            />
          </ButtonContext.Provider>
        )}
      </div>

      <FieldError>
        {errorMessage && (
          <ValidationMessage status="error" data-testid={`${dataTestId}/error`}>
            {errorMessage}
          </ValidationMessage>
        )}
      </FieldError>
    </SearchField>
  )
}
